import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { applyFieldErrors, ErrorState } from '@nucleus-commerce/admin-kit/errors';
import { FormSection } from '@nucleus-commerce/admin-kit/forms';
import { useSaveBar } from '@nucleus-commerce/admin-kit/save-bar';
import type { MemberChatProfile } from '@nucleus-commerce/api-client/admin';
import {
  Form,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@nucleus-commerce/ui/components/form';
import { Skeleton } from '@nucleus-commerce/ui/components/skeleton';

import { MediaIdField } from '@/features/media';
import { useI18nResolver } from '@/shared/forms/use-i18n-resolver';

import { fromChatProfile, toChatProfile } from '../api/mappers';
import { useChatProfile } from '../hooks/use-chat-profile';
import { useServerMessage } from '../hooks/use-server-message';
import { useUpdateChatProfile } from '../hooks/use-update-chat-profile';
import { chatProfileSchema, type ChatProfileForm } from '../schemas/chat-profile-schema';

import { FormAlert, SaveButton, TextField } from './form-controls';

const KNOWN_FIELDS: ReadonlySet<string> = new Set(['displayName', 'avatarMediaId']);

function ProfileForm({
  storeId,
  storeName,
  firstName,
  profile,
  readOnly,
}: {
  storeId: string;
  storeName: string;
  firstName: string;
  profile: MemberChatProfile;
  readOnly: boolean;
}) {
  const { t } = useTranslation('chatSettings');
  const serverMessage = useServerMessage();
  const update = useUpdateChatProfile(storeId);
  const form = useForm<ChatProfileForm>({
    resolver: useI18nResolver(chatProfileSchema),
    defaultValues: fromChatProfile(profile),
  });

  const submit = form.handleSubmit(async (values) => {
    form.clearErrors('root.server');
    try {
      await update.mutateAsync(toChatProfile(values));
      form.reset(values);
    } catch (error) {
      if (applyFieldErrors(error, form.setError, { knownFields: KNOWN_FIELDS })) return;
      form.setError('root.server', { type: 'server', message: serverMessage(error) });
    }
  });
  useSaveBar({ form, onSave: submit, saving: update.isPending, disabled: readOnly });

  return (
    <Form {...form}>
      <form
        onSubmit={submit}
        noValidate
        className="flex flex-col gap-4"
        aria-label={t('profile.formLabel', { store: storeName })}
      >
        <FormAlert message={form.formState.errors.root?.server?.message} />
        <TextField
          control={form.control}
          name="displayName"
          label={t('profile.displayName')}
          placeholder={firstName}
          description={t('profile.displayNameHint', { name: firstName })}
          disabled={readOnly}
        />
        <FormField
          control={form.control}
          name="avatarMediaId"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('profile.avatar')}</FormLabel>
              <MediaIdField
                value={field.value}
                onChange={field.onChange}
                label={t('profile.avatar')}
                disabled={readOnly}
              />
              <FormDescription>{t('profile.avatarHint')}</FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />
        <SaveButton saving={update.isPending} dirty={form.formState.isDirty} hidden={readOnly} />
      </form>
    </Form>
  );
}

/** How the member appears to one store's shoppers: a per-store chat name and avatar (Q20). */
export function ChatProfileCard({
  storeId,
  storeName,
  firstName,
  readOnly,
}: {
  storeId: string;
  storeName: string;
  firstName: string;
  readOnly: boolean;
}) {
  const query = useChatProfile(storeId);
  return (
    <FormSection title={storeName}>
      {query.isError ? (
        <ErrorState error={query.error} onRetry={() => void query.refetch()} compact />
      ) : query.data ? (
        <ProfileForm
          storeId={storeId}
          storeName={storeName}
          firstName={firstName}
          profile={query.data}
          readOnly={readOnly}
        />
      ) : (
        <Skeleton className="h-32" aria-busy="true" />
      )}
    </FormSection>
  );
}
